/**
 * Soft residual Page199 — Settings Danger (Page118/61 + dual).
 * Mount: /settings/danger
 * Soft residual deleted:
 *  - invent seats while posture loading from client role
 *  - invent superadmin close success
 *  - invent tenant close without ack
 *  - invent grace period / undo
 *  - paint wire paths or engineer honesty tags in operator DOM
 */
import { useState } from "react";
import { html, Ico, Card, Field, Input, Alert, KV, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

/** Missing contract ops must not crash the desk. */
function safeDangerPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_danger_status") return "/settings/danger/status";
    if (op === "post__auth_me_delete") return "/auth/me/delete";
    return "";
  }
}

/** Path-free operator copy for Danger desk. */
export function softDangerSettingsErrorOf(raw, fallback) {
  const m = String(raw || "").trim();
  const fb = fallback || "That action could not be completed.";
  if (!m) return fb;
  if (/\b401\b|session ended|unauthorized/i.test(m)) {
    return "Sign in again — your session is no longer valid.";
  }
  if (/\b403\b|forbidden|insufficient|not_authorized/i.test(m)) {
    return "This action is not allowed for your account.";
  }
  if (/\b400\b|confirm|password|ack|tenant|superadmin/i.test(m)) {
    return "The server refused that close request. Check password, confirmation, and acknowledgements.";
  }
  if (/\b429\b|too many/i.test(m)) {
    return "Too many attempts. Wait a moment, then try again.";
  }
  if (/\b5\d\d\b|timed out|network|unreachable|failed to fetch/i.test(m)) {
    return "The server did not answer. Try again shortly.";
  }
  if (/\/api\/|\/auth\/|\/settings\/danger|GET |POST |status_path|http/i.test(m)) {
    return fb;
  }
  return m.length > 160 ? fb : m;
}

/** Pure: seat chips from Live Settings Danger posture. Fail-closed. */
export function dangerSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  return {
    selfDeleteAllowed: p.self_delete_allowed === true,
    superadminBlocked: p.superadmin_self_delete_blocked === true,
    tenantAckRequired: p.tenant_ack_required === true,
    closesTenant: p.closes_tenant === true,
    dangerHonestyAllowed: p.danger_honesty_allowed === true,
    passwordNavAllowed: p.password_nav_allowed === true,
    sessionsNavAllowed: p.sessions_nav_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    confirmExpected: String(p.confirm_text_expected || "DELETE"),
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim() || null,
    softTitle: String(p.soft_title || "").trim() || null,
    softMessage: String(p.soft_message || "").trim() || null,
    enabled: p.enabled === true,
    forbidApiPaint: p.soft_api_path_paint_forbidden !== false,
  };
}

/** Pure: role-shaped danger KPIs prefer Live seat when present. */
export function dangerDeskKpisOf(me, seat) {
  const role = String((me && me.role) || (seat && seat.role) || "");
  const isSa = !!(seat && seat.superadminBlocked) || role === "superadmin";
  const isAdmin = !!(seat && seat.closesTenant);
  const isStaff = role === "sa_staff";
  const canSelfDelete = seat ? seat.selfDeleteAllowed === true : false;
  return {
    role: role || "—",
    username: (me && me.username) || "—",
    canSelfDelete,
    closesTenant: isAdmin,
    staffNote: isStaff,
    isSa,
  };
}

export function DangerSettingsDesk({ toast }) {
  const { me, signOut, reload } = useSession();
  const posture = useApi(safeDangerPath("get__settings_danger_status"));
  const post = posture.data || {};
  // Fail-closed while loading — Soft invent open write from client role deleted.
  const seat = dangerSettingsSeatOf(posture.loading || !posture.data ? {} : post);
  const { run, busy, error, envelope } = useAction();
  const [txt, setTxt] = useState("");
  const [password, setPassword] = useState("");
  const [ackTenant, setAckTenant] = useState(false);
  const [lastRefuse, setLastRefuse] = useState("");

  const kpis = dangerDeskKpisOf(me, seat);
  const okConfirm = txt.trim().toUpperCase() === String(seat.confirmExpected || "DELETE");
  const isAdmin = !!seat.closesTenant;
  const blockedSa = !!seat.superadminBlocked;
  const writeOpen = seat.selfDeleteAllowed === true;
  const softTitle = seat.softTitle || seat.postureLabel || "Close account";
  const softLede = seat.softMessage || seat.postureLabel || (
    "Close this account permanently. The server re-checks password and confirmation. "
    + "There is no undo once the server accepts the close."
  );

  const ready = okConfirm
    && !!password
    && writeOpen
    && (!seat.tenantAckRequired || ackTenant)
    && !busy;

  const reRead = () => {
    if (!seat.reReadAllowed) {
      toast && toast("You cannot re-read this desk from this account", "warn");
      return;
    }
    posture.reload && posture.reload();
    reload && reload();
    toast && toast("Danger desk refreshed", "go");
  };

  const close = async () => {
    if (!writeOpen) {
      toast(
        blockedSa
          ? "Superadmin accounts cannot be closed from this desk"
          : "Close-account is sealed for this account",
        "warn",
      );
      return;
    }
    if (!okConfirm) {
      toast("Type DELETE exactly to confirm", "bad");
      return;
    }
    if (!password) {
      toast("Enter your password to continue", "bad");
      return;
    }
    if (seat.tenantAckRequired && !ackTenant) {
      toast("Organisation admins must acknowledge tenant closure", "warn");
      return;
    }
    setLastRefuse("");
    const out = await run(safeDangerPath("post__auth_me_delete"), {
      method: "POST",
      body: {
        password,
        confirm_text: "DELETE",
        acknowledge_admin_tenant_closure: !!isAdmin && ackTenant,
      },
    });
    if (out) {
      toast(
        isAdmin
          ? "Organisation and account closed"
          : "Account closed",
        "warn",
      );
      setTxt("");
      setPassword("");
      setAckTenant(false);
      try {
        await signOut();
      } catch (_) {
        /* cookie may already be gone */
      }
      try {
        go("/auth/login");
      } catch (_) { /* hash shell */ }
      return;
    }
    const refuse = softDangerSettingsErrorOf(error, "Close was refused — nothing was changed");
    setLastRefuse(refuse);
    toast(refuse, "bad");
  };

  if (!me) {
    return html`<div className="danger-set-desk danger-set-desk--liquid"
      data-cmc-page="page118-danger" data-cmc-danger="page61" data-cmc-residual="page199-danger"
      data-soft-api-path-paint-forbidden=${seat.forbidApiPaint ? "1" : "0"}>
      <${Alert} tone="warn" title="No session" say="Sign in again to continue."/>
    </div>`;
  }

  if (!posture.loading && !seat.selfDeleteAllowed && !seat.dangerHonestyAllowed) {
    return html`<div className="danger-set-desk danger-set-desk--liquid"
      data-cmc-page="page118-danger" data-cmc-danger="page61" data-cmc-residual="page199-danger"
      data-soft-api-path-paint-forbidden=${seat.forbidApiPaint ? "1" : "0"}>
      <${Alert} tone="bad" icon="ban" title=${softTitle}
        say=${softLede}/>
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${!seat.reReadAllowed || posture.loading} onClick=${reRead}>
        <${Ico} d="refresh" w=${14}/> Refresh
      </button>
    </div>`;
  }

  return html`<div className="danger-set-desk danger-set-desk--liquid"
    data-cmc-page="page118-danger" data-cmc-danger="page61" data-cmc-residual="page199-danger"
    data-soft-api-path-paint-forbidden=${seat.forbidApiPaint ? "1" : "0"}>
    <div className="danger-set-desk__rail">
      <div>
        <p className="eyebrow">Settings · Danger</p>
        <h2 className="danger-set-desk__title">${softTitle}</h2>
        <p className="note">${softLede}</p>
      </div>
      <div className="btn-row danger-set-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.reReadAllowed || posture.loading}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.sessionsNavAllowed}
          onClick=${() => go("/settings/sessions")}>Sessions</button>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.passwordNavAllowed}
          onClick=${() => go("/settings/security")}>Password</button>
      </div>
    </div>

    <${Alert} tone="info" title="Server-checked close only"
      say=${`Signed in as ${seat.role || kpis.role || "operator"}. `
        + `Close ${writeOpen ? "open" : "sealed"} · warnings ${seat.dangerHonestyAllowed ? "open" : "sealed"}. `
        + `No grace period — nothing is invented when the server refuses.`}/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="danger-set-desk__kpis" role="group" aria-label="Danger posture">
      <div className="danger-set-desk__kpi" data-on=${seat.selfDeleteAllowed ? "1" : "0"}>
        <span>Close-account</span>
        <b>${seat.selfDeleteAllowed ? "open" : "sealed"}</b>
        <em>write</em>
      </div>
      <div className="danger-set-desk__kpi" data-on=${seat.dangerHonestyAllowed ? "1" : "0"}>
        <span>Warnings</span>
        <b>${seat.dangerHonestyAllowed ? "open" : "sealed"}</b>
        <em>independent</em>
      </div>
      <div className="danger-set-desk__kpi" data-on=${kpis.canSelfDelete ? "1" : "0"}>
        <span>This account</span>
        <b>${kpis.canSelfDelete ? "may close" : "blocked"}</b>
        <em>${kpis.username}</em>
      </div>
      <div className="danger-set-desk__kpi" data-on=${kpis.closesTenant ? "1" : "0"}>
        <span>Organisation</span>
        <b>${kpis.closesTenant ? "also closes" : "stays"}</b>
        <em>${seat.role || kpis.role}</em>
      </div>
    </div>
    ${posture.error ? html`
      <${Alert} tone="warn" title="Could not read danger posture"
        say=${softDangerSettingsErrorOf(posture.error, "Status refused for this account.")}/>` : null}

    ${blockedSa ? html`
      <${Alert} tone="warn" title="Superadmin cannot close here"
        say="Platform superadmin accounts stay open on this desk by design. Close only after the server confirms."/>` : null}

    ${isAdmin ? html`
      <${Alert} tone="warn" title="Organisation admin closes the whole tenant"
        say="With acknowledgement: owned devices return to factory, subordinate users are removed, and the email is released. There is no undo."/>` : null}

    ${kpis.staffNote ? html`
      <${Alert} tone="info" title="Platform staff account"
        say="Staff self-close removes the staff identity only — not an organisation admin tree."/>` : null}

    <${Card} cls="set__danger"
        title=${isAdmin ? "Close this account and organisation" : "Delete this account"}
        sub="Type DELETE exactly. Password is re-checked on the server. Audit history may remain; this login goes away.">
      <${Alert} title="No undo · no grace period"
        say="Type DELETE — not a marketing phrase. No recovery window is invented here."/>

      <div className="grid g2" style=${{ marginBottom: 12 }}>
        <${KV} k="Confirm match" v=${okConfirm ? seat.confirmExpected : "—"}/>
        <${KV} k="Password entered" v=${password ? "yes" : "no"}/>
        ${isAdmin ? html`<${KV} k="Tenant ack" v=${ackTenant ? "yes" : "no"}/>` : null}
        <${KV} k="Write seat" v=${writeOpen ? "open" : "sealed"}/>
      </div>

      <${Field} label="Type DELETE to confirm"
          hint=${txt && !okConfirm ? "Must be exactly DELETE." : "Compared exactly by the server."}
          bad=${!!txt && !okConfirm}>
        <${Input} icon="alert" value=${txt}
          disabled=${!writeOpen}
          onInput=${(e) => setTxt(e.target.value)}
          placeholder="DELETE" bad=${!!txt && !okConfirm} autocomplete="off"/>
      <//>
      <${Field} label="Your password">
        <${Input} icon="lock" type="password" value=${password}
          disabled=${!writeOpen}
          onInput=${(e) => setPassword(e.target.value)}
          placeholder="••••••••••••" autocomplete="current-password"/>
      <//>

      ${isAdmin ? html`
        <label className="danger-set-desk__ack" style=${{ display: "flex", gap: 10, alignItems: "flex-start", margin: "12px 0" }}>
          <input type="checkbox" checked=${ackTenant}
            onChange=${(e) => setAckTenant(!!e.target.checked)}
            disabled=${busy || !writeOpen}/>
          <span>
            <b>I acknowledge organisation closure</b>
            <span className="note" style=${{ display: "block", marginTop: 4 }}>
              Devices will be unclaimed and subordinate users removed when the server accepts this close.
            </span>
          </span>
        </label>` : null}

      ${lastRefuse ? html`
        <${Alert} tone="warn" title="Last refusal"
          say=${lastRefuse}/>` : null}

      <button type="button" className="btn btn--danger btn--wide"
        disabled=${!ready}
        onClick=${close}
        title=${!writeOpen ? "Close-account sealed" : "Close account permanently"}>
        ${busy
          ? "Closing…"
          : (isAdmin ? "Close account + organisation" : "Close permanently")}
      </button>
      <p className="note" style=${{ marginTop: 10 }}>
        Prefer ending a single browser session from Profile / Sign out — danger is irreversible account removal.
      </p>
    <//>
  </div>`;
}

export default DangerSettingsDesk;
